import React from "react";

/**
 * Bespoke, editorial panel chrome for the Shift Analysis dashboard.
 *
 * Borderless surface sitting on the page canvas, with a faint header strip
 * carrying a small-caps, wide-tracked label (+ optional meta / right slot).
 * Deliberately not a generic bordered card.
 */
interface PanelProps {
  label: string;
  meta?: React.ReactNode;
  right?: React.ReactNode;
  /** Accent color for the label tick. */
  accent?: string;
  className?: string;
  bodyClassName?: string;
  children: React.ReactNode;
}

const Panel: React.FC<PanelProps> = ({
  label,
  meta,
  right,
  accent = "var(--text-secondary)",
  className,
  bodyClassName,
  children,
}) => (
  <section
    className={`relative rounded-[20px] overflow-hidden h-full flex flex-col ${className ?? ""}`}
    style={{
      backgroundColor: "var(--bg-primary)",
      boxShadow:
        "0 1px 1px rgba(16,24,40,0.04), 0 10px 30px -12px rgba(16,24,40,0.18)",
    }}
  >
    <div
      className="flex items-center justify-between gap-3 px-5 pt-4 pb-3"
      style={{
        borderBottom: "1px solid var(--border-primary)",
      }}
    >
      <div className="flex items-baseline gap-2 min-w-0">
        <span
          className="inline-block w-1.5 h-1.5 rounded-full shrink-0 -translate-y-px"
          style={{ backgroundColor: accent }}
        />
        <span
          className="text-[10px] font-semibold uppercase tracking-[0.2em]"
          style={{ color: "var(--text-secondary)" }}
        >
          {label}
        </span>
        {meta && (
          <span
            className="text-[11px] truncate font-normal normal-case tracking-normal"
            style={{ color: "var(--text-secondary)", opacity: 0.8 }}
          >
            {meta}
          </span>
        )}
      </div>
      {right}
    </div>
    <div className={`flex-1 px-5 py-4 ${bodyClassName ?? ""}`}>{children}</div>
  </section>
);

export default Panel;
